import { useId, useRef, useState, type FormEvent } from 'react';
import { BanIcon, PenLineIcon, UnlockIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSubmit } from '../../hooks/useSubmit';
import { useRequestId } from '../../hooks/useRequestId';
import { newRequestId } from '../../core/api/http';
import { blockVoucher, correctVoucher } from '../adminApi';
import { Field } from '../ui/Field';
import { Spinner } from '../../ui/Spinner';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { received } from '../inbox/inboxRules';
import { money } from '../../core/format/currency';
import { parseEuroInput, signedAmount } from './voucherLabels';
import type { Voucher } from '../../core/types/vouchers';
type Props = {
  voucher: Voucher;
  onChanged: (next: Voucher) => void;
};
type Open = 'correct' | 'block' | null;

/**
 * Wat Sarah met een bon kan doen als er iets mis is: het saldo corrigeren (een correctieregel met
 * reden; het grootboek verandert nooit achteraf) of de bon blokkeren (kwijt, gestolen, verkeerd
 * uitgegeven). Een blokkade is op te heffen; het saldo blijft staan en telt als openstaand.
 */
export function VoucherActions({
  voucher: v,
  onChanged
}: Props) {
  const [open, setOpen] = useState<Open>(null);
  const expired = v.status === 'verlopen';
  if (expired) return <p className="text-[13px] text-muted" data-id="element-5329" data-mp="1k7gjg25eloli">Verlopen: deze bon is niet meer te corrigeren of te blokkeren.</p>;
  const done = (next: Voucher, message: string) => {
    onChanged(next);
    setOpen(null);
    toast.success(message);
  };
  return <div className="space-y-3" data-id="element-5330" data-mp="1b8v38dnemlnw">
      {v.blocked && <p className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-5331" data-mp="x80tnromeenn">
          Geblokkeerd sinds {received(v.blocked.at)}: {v.blocked.reason}
        </p>}
      {open === 'correct' ? <CorrectionForm voucher={v} onClose={() => setOpen(null)} onDone={next => done(next, 'Saldo gecorrigeerd')} data-id="element-5332" data-mp="2rla9003ukudt" /> : open === 'block' ? <BlockForm voucher={v} onClose={() => setOpen(null)} onDone={next => done(next, next.blocked ? 'Bon geblokkeerd' : 'Blokkade opgeheven')} data-id="element-5333" data-mp="2vl16emnvq8rb" /> : <div className="flex flex-wrap gap-2" data-id="element-5334" data-mp="3ejrzizk93wp">
          <button type="button" onClick={() => setOpen('correct')} className={btnSecondary} data-id="element-5335" data-mp="3hxv4tg1th57">
            <PenLineIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5336" data-mp="3vli2466hrs6g" />
            Saldo corrigeren
          </button>
          <button type="button" onClick={() => setOpen('block')} className={btnSecondary} data-id="element-5337" data-mp="r5p901h8m28l">
            {v.blocked ? <UnlockIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5338" data-mp="n32ettx3g588" /> : <BanIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5339" data-mp="icvqlv6humqi" />}
            {v.blocked ? 'Blokkade opheffen' : 'Blokkeren'}
          </button>
        </div>}
    </div>;
}
type FormProps = {
  voucher: Voucher;
  onClose: () => void;
  onDone: (next: Voucher) => void;
};
function CorrectionForm({
  voucher: v,
  onClose,
  onDone
}: FormProps) {
  const amountId = useId();
  const reasonId = useId();
  const [direction, setDirection] = useState<1 | -1>(-1);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(correctVoucher);
  const raw = parseEuroInput(amount);
  const value = raw === null ? 0 : raw * direction;
  const amountProblem = raw === null || raw <= 0 ? 'Vul een bedrag in, bijv. 15 of 12,50.' : v.balanceCents + value < 0 ? `Er staat ${money(v.balanceCents)} op; zoveel kan er niet af.` : null;
  const reasonProblem = reason.trim() ? null : 'Een reden is verplicht; die staat in de geschiedenis.';
  const attempt = `${value}|${reason.trim()}`;
  const requestId = useRequestId(attempt);
  const amountError = fieldErrors.amountCents ?? (tried ? amountProblem : null);
  const reasonError = fieldErrors.reason ?? (tried ? reasonProblem : null);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (amountProblem) return document.getElementById(amountId)?.focus();
    if (reasonProblem) return document.getElementById(reasonId)?.focus();
    const next = await submit(v.id, {
      requestId,
      amountCents: value,
      reason: reason.trim()
    });
    if (next) onDone(next);
  };
  return <form onSubmit={e => void onSubmit(e)} noValidate className="space-y-4 rounded-md bg-shell p-3.5" data-id="element-5340" data-mp="2g787w4ok4x1s">
      <label className="block" data-id="element-5341" data-mp="2b8dj8m3wse9r">
        <span className="sr-only" data-id="element-5342" data-mp="1nfx2cevwqsc1">Erbij of eraf</span>
        <select value={direction} onChange={e => setDirection(e.target.value === '1' ? 1 : -1)} className={inputCls} data-id="element-5343" data-mp="1nij6e4jlappi">
          <option value="-1" data-id="element-5344" data-mp="1xemk87nt0csa">Eraf</option>
          <option value="1" data-id="element-5345" data-mp="1xfny0jw7fddp">Erbij</option>
        </select>
      </label>
      <Field label="Bedrag (€)" data-id="element-5346" data-mp="1sf05tuw8oqhw">
        <input id={amountId} inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} aria-invalid={amountError ? true : undefined} aria-describedby={amountError ? `${amountId}-error` : undefined} className={`${inputCls} tabular-nums`} data-id="element-5347" data-mp="1sertc4413n78" />
      </Field>
      {amountError && <p id={`${amountId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-5348" data-mp="umaa0g889fuv">
          {amountError}
        </p>}
      <Field label="Reden" hint="Bijvoorbeeld: in de salon gebruikt zonder kassa, of fout bij uitgifte." data-id="element-5349" data-mp="gnkbqx9o9a4x">
        <input id={reasonId} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? `${reasonId}-error` : undefined} className={inputCls} data-id="element-5350" data-mp="llvmda7sjoo7" />
      </Field>
      {reasonError && <p id={`${reasonId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-5351" data-mp="14jy2t0m9wdae">
          {reasonError}
        </p>}
      {error && !fieldErrors.amountCents && !fieldErrors.reason && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-5352" data-mp="1x08un6q29vj4">
          {error}
        </p>}
      <div className="flex flex-wrap gap-2" data-id="element-5353" data-mp="26wr3z4a5kg55">
        <button type="submit" disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-5354" data-mp="26uep4zzoylwb">
          {submitting && <Spinner data-id="element-5355" data-mp="1wybf77ylliyt" />}
          {submitting ? 'Bezig…' : raw && !amountProblem ? `${signedAmount('cents', value)} opslaan` : 'Opslaan'}
        </button>
        <button type="button" onClick={onClose} disabled={submitting} className={btnSecondary} data-id="element-5356" data-mp="1ryfat7lznpiq">
          Niet corrigeren
        </button>
      </div>
    </form>;
}
function BlockForm({
  voucher: v,
  onClose,
  onDone
}: FormProps) {
  const reasonId = useId();
  const blocking = !v.blocked;
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(blockVoucher);
  const reasonProblem = blocking && !reason.trim() ? 'Waarom blokkeer je hem? Dat zie je later terug.' : null;
  const request = useRef(newRequestId());
  const reasonError = fieldErrors.reason ?? (tried ? reasonProblem : null);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (reasonProblem) return document.getElementById(reasonId)?.focus();
    const next = await submit(v.id, {
      requestId: request.current,
      blocked: blocking,
      reason: reason.trim()
    });
    if (next) onDone(next);
  };
  return <form onSubmit={e => void onSubmit(e)} noValidate className="space-y-4 rounded-md bg-shell p-3.5" data-id="element-5357" data-mp="3pu45dy5orswm">
      <p className="text-[13px] text-ink" data-id="element-5358" data-mp="2s8q0x6du5rtf">
        {blocking ? 'Een geblokkeerde bon werkt niet meer in de kassa en de webshop. Het saldo blijft staan; opheffen kan altijd.' : 'Na opheffen werkt de bon weer gewoon, met het saldo dat erop staat.'}
      </p>
      <Field label={blocking ? 'Reden' : 'Reden (optioneel)'} hint={blocking ? 'Bijvoorbeeld: kwijt volgens de klant, of per ongeluk twee keer uitgegeven.' : undefined} data-id="element-5359" data-mp="36wkv6r7hs48m">
        <input id={reasonId} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? `${reasonId}-error` : undefined} className={inputCls} data-id="element-5360" data-mp="2eg0fp464sqxd" />
      </Field>
      {reasonError && <p id={`${reasonId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-5361" data-mp="1guxltpp6hfjh">
          {reasonError}
        </p>}
      {error && !fieldErrors.reason && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-5362" data-mp="1qr0jai934q03">
          {error}
        </p>}
      <div className="flex flex-wrap gap-2" data-id="element-5363" data-mp="1lro14at5s73u">
        <button type="submit" disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-5364" data-mp="1zjufbrcr440n">
          {submitting && <Spinner data-id="element-5365" data-mp="1viijv5774okj" />}
          {submitting ? 'Bezig…' : blocking ? 'Blokkeren' : 'Blokkade opheffen'}
        </button>
        <button type="button" onClick={onClose} disabled={submitting} className={btnSecondary} data-id="element-5366" data-mp="29h8gyku0mo8w">
          Annuleren
        </button>
      </div>
    </form>;
}